import {fromJust} from '@freckle/maybe'
import {formatRoundedNumber} from '@freckle/educator-entities/ts/common/helpers/common-table-helper'
import type {RlStandardSetT} from '@freckle/educator-entities/ts/common/models/rl-standard-set'
import type {RlDomainIdT} from '@freckle/educator-entities/ts/common/types/rl-domain-id'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import type {BaseTeacherAttrs} from '@freckle/educator-entities/ts/users/models/teacher'
import {Table} from '@freckle/educator-materials/ts/components/table/table'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {SafeTrans} from '@freckle/educator-materials/ts/helpers/translate/component'
import {
  cellFromRow,
  aligned,
  idAccessorFromProp,
  disableSortBy
} from '@freckle/educator-materials/ts/logic/table/table'
import type {ProficiencyReportTableRowT} from '@freckle/school/ts/proficiency-report/logic/proficiency-report-table-logic'
import type {ProficiencyReportStudentGroupsT} from '@freckle/school/ts/proficiency-report/models/proficiency-report'
import type {StudentsMapT} from '@freckle/school/ts/users/collections/students'
import {ProficiencyReportExpandableRow} from './expandable-row'
import {formatProgressBar} from './utils'

type Row = ProficiencyReportTableRowT

type Props = {
  tableData: Array<ProficiencyReportTableRowT>
  students: StudentsMapT
  teachers: Array<BaseTeacherAttrs>
  schools: Array<SchoolAttrs>
  selectedDomainId: RlDomainIdT
  selectedGrade: number
  mathStandardSet: RlStandardSetT
}

export default function ProficiencyReportTable({
  students,
  tableData,
  teachers,
  selectedGrade,
  selectedDomainId,
  mathStandardSet,
  schools
}: Props) {
  const {t} = useSafeTranslation()
  return (
    <div className="bootstrap-table-container proficiency-report-table-wrapper">
      <Table
        data={tableData}
        SubRow={(row: Row) => (
          <ProficiencyReportExpandableRow
            tableRow={row}
            students={students}
            teachers={teachers}
            selectedGrade={selectedGrade}
            selectedDomainId={selectedDomainId}
            mathStandardSet={mathStandardSet}
          />
        )}
        columns={[
          {
            Header: t('PROFICIENCY_REPORT_TABLE_COLUMN_HEADER_SCHOOL'),
            Cell: cellFromRow((row: Row) => <SchoolName row={row} schools={schools} />),
            ...idAccessorFromProp('schoolId')
          },
          {
            Header: t('PROFICIENCY_REPORT_TABLE_COLUMN_HEADER_GRADE_LEVEL'),
            accessor: (d: Row) => formatRoundedNumber(2)(d.averageGradeLevel),
            id: 'level'
          },
          {
            Header: <SafeTrans i18nKey="PROFICIENCY_REPORT_TABLE_COLUMN_HEADER_BELOW" />,
            accessor: (d: Row) => d.studentGroups.belowGradeLevel.length,
            id: 'below'
          },
          {
            Header: <SafeTrans i18nKey="PROFICIENCY_REPORT_TABLE_COLUMN_HEADER_WITHIN" />,
            accessor: (d: Row) => d.studentGroups.withinGradeLevel.length,
            id: 'within'
          },
          {
            Header: <SafeTrans i18nKey="PROFICIENCY_REPORT_TABLE_COLUMN_HEADER_ABOVE" />,
            accessor: (d: Row) => d.studentGroups.aboveGradeLevel.length,
            id: 'above'
          },
          {
            accessor: (d: Row) => d.studentGroups,
            Cell: cellFromRow(({studentGroups}: Row) => (
              <StudentsProgressBar studentGroups={studentGroups} />
            )),
            id: 'progress-bar',
            ...aligned('center'),
            disableSortBy
          }
        ].map(c => ({...aligned('left'), ...c, tdClassName: () => 'proficiency-report-table-row'}))}
      />
    </div>
  )
}

const SchoolName = ({schools, row: {schoolId}}: {schools: SchoolAttrs[]; row: Row}) => {
  const mSchool = schools.find(school => school.id === schoolId)
  const school = fromJust(mSchool, `Unable to find schoolId: ${schoolId} in schools`)

  return <>{school.name}</>
}

const StudentsProgressBar = ({
  studentGroups: {belowGradeLevel, withinGradeLevel, aboveGradeLevel}
}: {
  studentGroups: ProficiencyReportStudentGroupsT
}) => {
  const {t} = useSafeTranslation()
  return formatProgressBar(
    belowGradeLevel.length,
    withinGradeLevel.length,
    aboveGradeLevel.length,
    t
  )
}
